<template>
  <SoDialog
    v-model="value"
    card-title="Confirm SMS Batch"
    persistent
    data-testid="sms-sender-confirm-dialog"
  >
    <div class="mb-4">
      <p class="text-body-1 mb-2">
        You are about to send an SMS to
        <strong>{{ validCount }}</strong> recipient{{
          validCount !== 1 ? "s" : ""
        }}.
      </p>
      <p class="text-body-2 mb-2">Message preview:</p>
      <div
        class="pa-3 bg-surface-variant rounded"
        data-testid="sms-confirm-message-preview"
      >
        {{ messageBody }}
      </div>
    </div>
    <div class="d-flex justify-space-between">
      <SoButton
        title="Cancel"
        large
        data-testid="sms-confirm-cancel-button"
        @click="value = false"
      />
      <SoButton
        title="Confirm & Send"
        type="primary"
        large
        :loading="createMutator.isPending"
        data-testid="sms-confirm-send-button"
        @click="handleConfirm"
      />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { SmsBatchMutator } from "@/mutators/smsBatch/SmsBatchMutator"
  import { errorHandler, errorReporter } from "@/services/ErrorService"

  export default defineComponent({
    components: { SoDialog, SoButton },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      file: {
        type: Object as PropType<File | null>,
        default: null,
      },
      messageBody: {
        type: String,
        default: "",
      },
      validCount: {
        type: Number,
        default: 0,
      },
    },
    emits: ["update:modelValue", "sent", "batch-sent"],
    data() {
      return {
        snackbar: useSnackbarStore(),
        createMutator: SmsBatchMutator.create(),
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
    },
    methods: {
      async handleConfirm() {
        if (!this.file) return

        try {
          await this.createMutator.mutateAsync({
            file: this.file,
            messageBody: this.messageBody,
          })

          this.snackbar.addSuccess("Your messages will be sent shortly.")
          this.value = false
          this.$emit("sent")
          this.$emit("batch-sent")
        } catch (error) {
          errorReporter.report(error)
          this.snackbar.addError(
            errorHandler.userMessage(
              error,
              "There was an error sending the SMS batch. Please try again.",
            ),
          )
        }
      },
    },
  })
</script>
